import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import './Media.css';

import type { Clip, Ratio } from '../content';

const mediaUrl = (id: string, ext: 'webm' | 'jpg') =>
  `${process.env.PUBLIC_URL}/media/${id}.${ext}`;

function prefersReducedMotion() {
  return (
    typeof window !== 'undefined' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}

/* ─── Lightbox plumbing ────────────────────────────────────── */

const LightboxContext = createContext<(clip: Clip) => void>(() => {});

export function LightboxProvider({ children }: { children: React.ReactNode }) {
  const [clip, setClip] = useState<Clip | null>(null);
  const close = useCallback(() => setClip(null), []);

  useEffect(() => {
    if (!clip) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    document.addEventListener('keydown', onKey);
    const { overflow } = document.body.style;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = overflow;
    };
  }, [clip, close]);

  return (
    <LightboxContext.Provider value={setClip}>
      {children}
      {clip && (
        <div className="lightbox" onClick={close} role="dialog" aria-modal="true" aria-label={clip.caption}>
          <button className="lightbox-close" onClick={close} aria-label="Close">
            &times;
          </button>
          <div className="lightbox-inner" onClick={(e) => e.stopPropagation()}>
            {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
            <video
              className="lightbox-video"
              src={mediaUrl(clip.id, 'webm')}
              poster={mediaUrl(clip.id, 'jpg')}
              controls
              autoPlay
              loop
              muted
              playsInline
            />
            <p className="lightbox-caption">{clip.caption}</p>
          </div>
        </div>
      )}
    </LightboxContext.Provider>
  );
}

/* ─── Lazy, self-pausing video ─────────────────────────────── */

/**
 * Loads its source only once it scrolls near the viewport, and pauses again
 * when it leaves. With two dozen looping clips on one page, leaving them all
 * decoding in the background is the difference between a quiet laptop and a
 * loud one.
 */
function LazyVideo({
  id,
  title,
  className = 'clip-video',
}: {
  id: string;
  title: string;
  className?: string;
}) {
  const ref = useRef<HTMLVideoElement>(null);
  const loaded = useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const still = prefersReducedMotion();

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          if (!loaded.current) {
            loaded.current = true;
            el.src = mediaUrl(id, 'webm');
          }
          if (!still) el.play().catch(() => {});
        } else if (loaded.current) {
          el.pause();
        }
      },
      { rootMargin: '150px 0px' }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [id]);

  return (
    // eslint-disable-next-line jsx-a11y/media-has-caption
    <video
      ref={ref}
      className={className}
      poster={mediaUrl(id, 'jpg')}
      preload="none"
      loop
      muted
      playsInline
      aria-label={title}
    />
  );
}

/* ─── Public pieces ────────────────────────────────────────── */

interface ClipFigureProps {
  clip: Clip;
  /** Fallback crop when the clip does not declare one of its own. */
  ratio?: Ratio;
  className?: string;
}

export function ClipFigure({ clip, ratio = '16/9', className = '' }: ClipFigureProps) {
  const open = useContext(LightboxContext);
  const box = clip.ratio ?? ratio;

  return (
    <figure className={`clip ${className}`}>
      <button
        type="button"
        className={`clip-frame clip-frame--${box.replace('/', '-')}`}
        onClick={() => open(clip)}
        aria-label={`Expand: ${clip.caption}`}
      >
        <LazyVideo id={clip.id} title={clip.caption} />
        <span className="clip-expand" aria-hidden="true">
          <svg viewBox="0 0 16 16" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="1.5">
            <path d="M6 1H1v5M10 15h5v-5M1 10v5h5M15 6V1h-5" />
          </svg>
        </span>
      </button>
      <figcaption className="clip-caption">{clip.caption}</figcaption>
    </figure>
  );
}

export function ClipGrid({
  clips,
  square = false,
  cols,
}: {
  clips: Clip[];
  square?: boolean;
  cols?: 1 | 2 | 3 | 4;
}) {
  const columns = cols ?? Math.min(clips.length, 3);
  return (
    <div className={`clip-grid clip-grid--${columns}`}>
      {clips.map((clip) => (
        <ClipFigure key={clip.id} clip={clip} ratio={square ? '1/1' : '16/9'} />
      ))}
    </div>
  );
}

export function StillFigure({
  src,
  alt,
  caption,
  ratio = '16/9',
}: {
  src: string;
  alt: string;
  caption?: string;
  ratio?: Ratio;
}) {
  return (
    <figure className="clip">
      <div className={`clip-frame clip-frame--${ratio.replace('/', '-')} clip-frame--static`}>
        <img src={src} alt={alt} loading="lazy" className="clip-video" />
      </div>
      {caption && <figcaption className="clip-caption">{caption}</figcaption>}
    </figure>
  );
}

/* ─── Photo / video band ───────────────────────────────────── */

export type BandMedia =
  | { kind: 'clip'; id: string; alt: string }
  | { kind: 'image'; src: string; alt: string };

/**
 * A full-bleed strip of media with copy laid over it.
 *
 * `cover` fills the strip and is the right choice for footage. `inset` places
 * the media at its own aspect against the right edge and feathers it, which
 * is what a tall photo needs, because cropping one to a wide strip just picks out
 * whichever band happens to fall in the middle.
 */
export function MediaBand({
  media,
  variant = 'cover',
  className = '',
  children,
}: {
  media: BandMedia;
  variant?: 'cover' | 'inset';
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <div className={`band band--${variant} ${className}`}>
      {media.kind === 'clip' ? (
        <LazyVideo id={media.id} title={media.alt} className="band-media" />
      ) : (
        <img src={media.src} alt={media.alt} className="band-media" loading="lazy" />
      )}
      <div className="band-scrim" aria-hidden="true" />
      <div className="wrap band-inner">{children}</div>
    </div>
  );
}
